export const ServerEvent = {
  TaskCompleted: "task_completed",
  ResetTask: "reset_task",
  SmsInviteSent: "sms_invite_sent",
  SmsInviteSentToExistingUser: "sms_invite_sent_to_existing_user",
  ProxyCommunication: "proxy_communication",
  ProxyCommunicationFromUnknownUser: "proxy_communication_from_unknown_user",
} as const;

export type ServerEvent = (typeof ServerEvent)[keyof typeof ServerEvent];

export type TaskCompletionView = "projects" | "todays_actions" | "bids" | "project_milestones" | "tasks";

export type ServerEventProperties = {
  [ServerEvent.TaskCompleted]: {
    taskType: string;
  };
  [ServerEvent.ResetTask]: {
    taskType: string;
  };
  [ServerEvent.SmsInviteSent]: {
    inviteePhone: string;
  };
  [ServerEvent.SmsInviteSentToExistingUser]: {
    inviteePhone: string;
  };
  [ServerEvent.ProxyCommunication]: {
    type: "call" | "text";
    provider: string;
    fromNumber: string;
    fromProxyNumber: string;
    toProxyNumber: string;
    toNumber: string;
    bidUrl: string | null;
    bidId: number | null;
    contractorOrganizationId: string | null;
  };
  [ServerEvent.ProxyCommunicationFromUnknownUser]: {
    type: "call" | "text";
    provider: string;
    fromNumber: string;
    toProxyNumber: string;
    toNumber: string;
    toPersonId: string | null;
  };
};

export const NextEvent = {
  TaskCompleted: "task_completed",
  SendAgreementSubmitted: "send_agreement_submitted",
  SettingsNotificationsToggleSuccess: "settings::notifications::toggle::success",
  QuotingClick: "quoting::click",
  QuotingSessionStarted: "quoting_session_started",
  QuotingSessionEnded: "quoting_session_ended",
  QuotingExternalReferenceClicked: "quoting_external_reference_clicked",
  QuotingTileSourceChanged: "quoting_tile_source_changed",
  OrderMaterialsSubmitted: "order_materials_submitted",
  SiteVisitSowCompleted: "site-visit::sow::completed",
  SiteVisitUnattendedSelected: "site-visit::unattended::selected",
  SiteVisitAvailabilityCompleted: "site-visit::availability::completed",
  SiteVisitContinueWithoutClicked: "site-visit::continue-without::clicked",
  BidWorksiteViewToggle: "bid::worksite-view::toggle",
  BidWorksiteViewViewed: "bid::worksite-view::viewed",
  ConversationResponded: "conversation_responded",
  ConversationPartnerRecipientsSend: "conversations::partner-recipients::send-with-recipients",
  ConversationSmsSuppressedSubmit: "conversations::send-sms::suppressed-submit",
  ConversationOpened: "conversation_opened",
  DispatchBoardReady: "dispatch::board::ready",
  PwaInstalled: "pwa_installed",
} as const;

export type NextEvent = (typeof NextEvent)[keyof typeof NextEvent];

import type { ConversationChannel } from "@remi/enums/conversation/channel";
import type { ConversationType } from "@remi/enums/conversation/type";

import type { Source } from "./source";

export type QuotingSessionOutcome = "submitted" | "saved" | "abandoned";
export type QuotingMapTileSource = "nearmap" | "google";
export type QuotingBidSource = Source | "UNKNOWN";
export type QuotingExternalReference = "google_maps" | "google_earth" | "partner_portal" | "bid_details" | "quote_pdf";
export type OrderMaterialsSyncSubmissionType = "sync_srs" | "sync_qxo" | "sync_abc";
type QuotingSessionStartReason = "first_interaction" | "version_rotated";
export type QuotingSessionEndReason = "submit" | "unload" | "unmount" | "version_rotated";

/**
 * `default` and `preference` are distinct on purpose: `preference` is an eligible user who chose a
 * view, `default` is one who never chose. Collapsing them loses adoption's denominator.
 */
export type BidWorksiteViewSource = "param" | "forced-classic" | "preference" | "default";

type ConversationResponseKind = "message" | "sms" | "call";
export type ConversationSurface = "feed" | "popout";
export type ConversationOpenTrigger = "user" | "auto";

export type QuotingCommonProperties = {
  quotingSessionId: string;
  bidId: number;
  partnerFlow: string;
  source: QuotingBidSource;
  isRll: boolean;
  contractorType: string | null;
  stateCode: string | null;
  bidCreatedAt: string;
  createQuoteTaskCreatedAt: string | null;
  firstQuoteSubmittedAt: string | null;
  priorSubmissionCount: number;
  quoteVersionId: string | null;
  quoteVersionCreatedAt: string | null;
  versionNumber: number;
  isInitialVersion: boolean;
  roofComplexity: string | null;
};

export type NextEventProperties = {
  [NextEvent.TaskCompleted]: {
    taskType: string;
    view?: TaskCompletionView;
  };
  [NextEvent.SendAgreementSubmitted]: {
    variant: "standard" | "sow_change";
  };
  [NextEvent.SettingsNotificationsToggleSuccess]: {
    action: "opt-in" | "opt-out";
    notification_type: string;
    channel: "inbox" | "toast" | "email" | "sms" | "push";
    previous_state: boolean;
    new_state: boolean;
  };
  [NextEvent.QuotingClick]: {
    requestForQuoteId: number;
  };
  [NextEvent.QuotingSessionStarted]: QuotingCommonProperties & {
    startReason: QuotingSessionStartReason;
    hasLidarData: boolean;
    hasStreetView: boolean;
    hasExistingSavedQuote: boolean;
    photoCount: number;
  };
  [NextEvent.QuotingSessionEnded]: QuotingCommonProperties & {
    outcome: QuotingSessionOutcome;
    endReason: QuotingSessionEndReason;
    mapTileSource: QuotingMapTileSource;
    totalDurationSec: number;
    activeDurationSec: number;
    idleDurationSec: number;
    backgroundDurationSec: number;
    drawingDurationSec: number;
    settingsDurationSec: number;
    mapDurationSec: number;
    photoDurationSec: number;
    streetViewDurationSec: number;
    lidarDurationSec: number;
    reviewDurationSec: number;
    polygonsDrawnCount: number;
    vertexClickCount: number;
    vertexEditCount: number;
    vertexDeleteCount: number;
    linesLabelledCount: number;
    settingsChangedCount: number;
    saveCount: number;
    lidarImportCount: number;
    lidarPolygonsImportedCount: number;
    lidarPolygonsAtSaveCount: number;
    polygonDeleteCount: number;
    lidarPolygonDeleteCount: number;
    undoCount: number;
    redoCount: number;
    timeToFirstInteractionMs: number | null;
    saveToSubmitSec: number | null;
  };
  [NextEvent.QuotingExternalReferenceClicked]: QuotingCommonProperties & {
    reference: QuotingExternalReference;
  };
  [NextEvent.QuotingTileSourceChanged]: QuotingCommonProperties & {
    fromSource: QuotingMapTileSource;
    toSource: QuotingMapTileSource;
  };
  [NextEvent.OrderMaterialsSubmitted]: {
    submissionType: OrderMaterialsSyncSubmissionType | "manual";
    orderId: string | null;
    projectId: number;
    contractorOrganizationId: string | null;
    contractorName: string | null;
  };
  [NextEvent.SiteVisitSowCompleted]: {
    withScheduling: boolean;
    bidId: number;
    projectId: number;
  };
  [NextEvent.SiteVisitUnattendedSelected]: {
    allowUnattended: boolean;
    bidId: number;
    projectId: number;
  };
  [NextEvent.SiteVisitAvailabilityCompleted]: {
    slotCount: number;
    unattended: boolean;
    outsideSla: boolean;
    homeownerRequestedDelay: boolean;
    bidId: number;
    projectId: number;
  };
  [NextEvent.SiteVisitContinueWithoutClicked]: {
    bidId: number;
    projectId: number;
  };
  [NextEvent.BidWorksiteViewToggle]: {
    view: "classic" | "worksite";
    source: BidWorksiteViewSource;
    bidId: number;
  };
  [NextEvent.BidWorksiteViewViewed]: {
    view: "classic" | "worksite";
    source: BidWorksiteViewSource;
    bidId: number;
  };
  [NextEvent.ConversationResponded]: {
    responseKind: ConversationResponseKind;
    channel: ConversationChannel;
    conversationId: string;
    isScheduled: boolean;
  };
  [NextEvent.ConversationPartnerRecipientsSend]: {
    conversationId: string;
    recipientCount: number;
  };
  [NextEvent.ConversationSmsSuppressedSubmit]: Record<string, never>;
  [NextEvent.ConversationOpened]: {
    channel: ConversationChannel;
    conversationType: ConversationType;
    conversationId: string;
    isResolved: boolean;
    surface: ConversationSurface;
    openTrigger: ConversationOpenTrigger;
  };
  [NextEvent.DispatchBoardReady]: {
    /** Only a page-load time when the board was the first page of the tab; after a client-side route change it includes time spent on earlier pages. */
    sinceTimeOriginMs: number;
    sinceMountMs: number;
    servedFromCache: boolean;
    poolSize: number;
    rescheduleCount: number;
    appointmentCount: number;
    crewCount: number;
  };
  [NextEvent.PwaInstalled]: Record<string, never>;
};

export const PortalEvent = {
  ConfirmContinue: "portal_confirm_continue_clicked",
  PrePtoConfirmContinue: "portal_pre_pto_confirm_continue_clicked",
  PostPtoConfirmContinue: "portal_post_pto_confirm_continue_clicked",
  SowRoofImageStepViewed: "portal_sow_roof_image_step_viewed",
  SowRoofImageLoaded: "portal_sow_roof_image_loaded",
  SowRoofImageFailed: "portal_sow_roof_image_failed",
} as const;

export type PortalEvent = (typeof PortalEvent)[keyof typeof PortalEvent];

export const MobileEvent = {
  AppLogin: "app_login",
  NeedPermissions: "need_permissions",
  OnboardingCompleted: "onboarding_completed",
  OnboardingExited: "onboarding_exited",
  ProjectViewed: "project_viewed",
  UndoCheckIn: "undo_check_in",
  FileViewed: "file_viewed",
  NotificationClicked: "notification_clicked",
  BottomSheetDismissed: "bottom_sheet_dismissed",
  VersionUpgradeInitiated: "version_upgrade_initiated",
  VersionUpgradeModalDisplayed: "version_upgrade_modal_displayed",
  // Camera
  PhotoCaptured: "photo_captured",
  MediaPickedFromLibrary: "media_picked_from_library",
  CameraQuestionAdvanced: "camera_question_advanced",
  // Checklist
  ChecklistSubmitted: "checklist_submitted",
  ChecklistStarted: "checklist_started",
  ChecklistTypeChanged: "checklist_type_changed",
  ChecklistStructureAdded: "checklist_structure_added",
  // Project Details
  ProjectTabChanged: "project_tab_changed",
  ProjectLinkCopied: "project_link_copied",
  EagleViewOpened: "eagleview_opened",
  RoofImageOpened: "roof_image_opened",
  WorkOrderOpened: "work_order_opened",
  // Navigation
  MenuItemPressed: "menu_item_pressed",
  // Onboarding
  OnboardingStepCompleted: "onboarding_step_completed",
  // Settings
  ProfileUpdated: "profile_updated",
  DeleteAccountInitiated: "delete_account_initiated",
  // Connectivity
  ConnectivityChanged: "connectivity_changed",
} as const;

export type MobileEvent = (typeof MobileEvent)[keyof typeof MobileEvent];

export type MobileEventProperties = {
  [MobileEvent.AppLogin]: Record<string, never>;
  [MobileEvent.NeedPermissions]: {
    hasLocationPermission?: boolean;
    hasNotificationPermission?: boolean;
  };
  [MobileEvent.OnboardingCompleted]: Record<string, never>;
  [MobileEvent.OnboardingExited]: Record<string, never>;
  [MobileEvent.ProjectViewed]: {
    projectId: number;
    inBottomSheet: boolean;
  };
  [MobileEvent.UndoCheckIn]: {
    projectId: number;
    inBottomSheet: boolean;
  };
  [MobileEvent.FileViewed]: {
    projectId: number;
    fileId: string;
  };
  [MobileEvent.NotificationClicked]: {
    projectId?: number;
  };
  [MobileEvent.BottomSheetDismissed]: {
    projectId: number;
  };
  [MobileEvent.VersionUpgradeInitiated]: Record<string, never>;
  [MobileEvent.VersionUpgradeModalDisplayed]: {
    currentVersion?: string;
    minimumSupportedVersion: string;
  };
  [MobileEvent.PhotoCaptured]: {
    projectId: number;
    questionId: string;
    mediaType: "photo" | "video";
    // The project's contractor org — which contractor's job this field-worker activity is for.
    contractorOrganizationId: string | null;
  };
  [MobileEvent.MediaPickedFromLibrary]: {
    projectId: number;
    questionId: string;
    count: number;
  };
  [MobileEvent.CameraQuestionAdvanced]: {
    projectId: number;
    questionId: string;
    action: "next" | "skip" | "done";
    // The project's contractor org — which contractor's job this field-worker activity is for.
    contractorOrganizationId: string | null;
  };
  [MobileEvent.ChecklistSubmitted]: {
    projectId: number;
    submissionId: string;
    isOffline: boolean;
    isIncomplete: boolean;
    // The project's contractor org — which contractor's job this field-worker activity is for.
    contractorOrganizationId: string | null;
  };
  [MobileEvent.ChecklistStarted]: {
    projectId: number;
    isResume: boolean;
  };
  // `from` is nullable because a schema row may carry a checklistType this build does not know.
  [MobileEvent.ChecklistTypeChanged]: {
    qaProjectId: number;
    coveredProjectId: number;
    category: string;
    from: string | null;
    to: string;
  };
  // coveredProjectId is null on a site visit, which has no covered projects.
  [MobileEvent.ChecklistStructureAdded]: {
    projectId: number;
    coveredProjectId: number | null;
    structureNumber: number;
  };
  [MobileEvent.ProjectTabChanged]: {
    projectId: number;
    tab: "overview" | "checklist" | "photos";
  };
  [MobileEvent.ProjectLinkCopied]: {
    projectId: number;
  };
  [MobileEvent.EagleViewOpened]: {
    projectId: number;
  };
  [MobileEvent.RoofImageOpened]: {
    projectId: number;
  };
  [MobileEvent.WorkOrderOpened]: {
    projectId: number;
    coveredProjectId: number;
  };
  [MobileEvent.MenuItemPressed]: {
    item:
      | "checked-in-jobs"
      | "checklist"
      | "settings"
      | "admin-tools"
      | "camera"
      | "terms-of-service"
      | "privacy-notice";
  };
  [MobileEvent.OnboardingStepCompleted]: {
    step: "legal" | "tutorial_checklists" | "tutorial_instructions" | "tutorial_complete" | "camera" | "notifications";
  };
  [MobileEvent.ProfileUpdated]: {
    field: "name";
  };
  [MobileEvent.DeleteAccountInitiated]: Record<string, never>;
  [MobileEvent.ConnectivityChanged]: {
    status: "online" | "offline";
  };
};
